import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  useTheme
} from '@mui/material';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { FaChartLine, FaCalendarAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import MainCard from 'components/MainCard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { getReport } from 'services/report/getReport';
import { startOfMonth, endOfMonth, eachDayOfInterval, getWeek, isSameDay, parseISO, startOfDay } from 'date-fns';

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function ProgressTracker() {
  const theme = useTheme();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(months[now.getMonth()]);
  const [viewType, setViewType] = useState('calendar');
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const studentId = localStorage.getItem('userid');
  const { data, isLoading, error } = useQuery({
    enabled: !!studentId,
    queryKey: ['reports', studentId],
    queryFn: () => getReport({ studentId: Number(studentId) })
  });
  const allReports = data?.data || [];

  // Filter reports for selected month/year
  const reportsForMonth = allReports.filter((report) => {
    const date = new Date(report.date);
    return date.getFullYear() === selectedYear && months[date.getMonth()] === selectedMonth;
  });

  // Calendar days
  const getDaysInMonth = (month, year) => {
    const monthIndex = months.indexOf(month);
    const start = startOfMonth(new Date(year, monthIndex));
    const end = endOfMonth(start);
    const days = eachDayOfInterval({ start, end });
    const firstDayOfWeek = start.getDay();
    const calendarDays = [];
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarDays.push({ day: '', status: 'empty' });
    }
    days.forEach((day) => {
      const reportsForDay = reportsForMonth.filter((r) => isSameDay(parseISO(r.date), day));
      const uniqueAsanas = [...new Set(reportsForDay.map((r) => r.asanaName))];
      let status;
      const today = new Date();
      if (reportsForDay.length > 0) {
        status = 'attended';
      } else if (day < startOfDay(today)) {
        status = 'not-attended';
      } else {
        status = 'no-session';
      }
      calendarDays.push({
        day: day.getDate(),
        status,
        accuracy:
          reportsForDay.length > 0 ? Math.round(reportsForDay.reduce((sum, r) => sum + r.performance, 0) / reportsForDay.length) : 0,
        poses: uniqueAsanas
      });
    });
    return calendarDays;
  };
  const calendarDays = getDaysInMonth(selectedMonth, selectedYear);

  // Weekly data for graphs
  const getWeeklyData = () => {
    const weeks = {};
    reportsForMonth.forEach((report) => {
      const week = getWeek(parseISO(report.date));
      if (!weeks[week]) weeks[week] = [];
      weeks[week].push(report);
    });
    return Object.entries(weeks).map(([week, reports]) => {
      const accuracy = Math.round(reports.reduce((sum, r) => sum + r.performance, 0) / reports.length);
      const sessions = reports.length;
      const participation = Math.round((sessions / 7) * 100);
      return {
        week: `Week ${week}`,
        accuracy,
        participation,
        sessions
      };
    });
  };
  const weeklyData = getWeeklyData();

  // Pose accuracy data
  const getPoseAccuracyData = () => {
    const poses = {};
    reportsForMonth.forEach((report) => {
      if (!poses[report.asanaName]) poses[report.asanaName] = [];
      poses[report.asanaName].push(report.performance);
    });
    return Object.entries(poses).map(([name, performances], i) => ({
      name,
      accuracy: Math.round(performances.reduce((a, b) => a + b, 0) / performances.length),
      color: [
        theme.palette.primary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main
      ][i % 5]
    }));
  };
  const poseAccuracyData = getPoseAccuracyData();

  // Summary statistics
  const totalSessions = reportsForMonth.length;
  const attendedSessions = totalSessions;
  const attendanceRate = calendarDays.filter((d) => d.day !== '').length
    ? Math.round((attendedSessions / calendarDays.filter((d) => d.day !== '').length) * 100)
    : 0;
  const averageAccuracy = totalSessions ? Math.round(reportsForMonth.reduce((sum, r) => sum + r.performance, 0) / totalSessions) : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'attended':
        return <CheckCircleOutlined style={{ color: theme.palette.success.main }} />;
      case 'not-attended':
        return <CloseCircleOutlined style={{ color: theme.palette.error.main }} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="error.main">
          Error loading reports. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: theme.palette.grey[50] }}>
      <Typography
        variant="h3"
        sx={{
          mb: 3,
          fontWeight: 700,
          color: theme.palette.primary.main,
          fontSize: { xs: '1.8rem', sm: '2.2rem' }
        }}
      >
        Progress Tracker
      </Typography>

      {/* Controls */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={(_, newValue) => {
            if (newValue) setViewType(newValue);
          }}
          size="small"
          sx={{
            bgcolor: theme.palette.grey[100],
            borderRadius: 2,
            '& .MuiToggleButton-root': {
              border: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: theme.palette.text.primary,
              px: 3,
              '&.Mui-selected': {
                bgcolor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  bgcolor: theme.palette.primary.main
                }
              },
              '&:hover': {
                bgcolor: theme.palette.grey[200]
              }
            }
          }}
        >
          <ToggleButton value="calendar">
            <FaCalendarAlt style={{ marginRight: 8 }} />
            Calendar View
          </ToggleButton>
          <ToggleButton value="graphs">
            <FaChartLine style={{ marginRight: 8 }} />
            Progress Graphs
          </ToggleButton>
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontWeight: 500 }}>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                borderRadius: 2,
                bgcolor: theme.palette.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300]
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.light
                }
              }}
            >
              <MenuItem value={2024}>2024</MenuItem>
              <MenuItem value={2025}>2025</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontWeight: 500 }}>Month</InputLabel>
            <Select
              value={selectedMonth}
              label="Month"
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{
                borderRadius: 2,
                bgcolor: theme.palette.grey[100],
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300]
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.light
                }
              }}
            >
              {months.map((month) => (
                <MenuItem key={month} value={month}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {viewType === 'calendar' && (
        <MainCard
          title="Session Calendar"
          sx={{
            mb: 4,
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.grey[200]}`
          }}
        >
          <CardContent>
            {/* Calendar Header */}
            <Grid container sx={{ mb: 2 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Grid item xs={12 / 7} key={day}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textAlign: 'center',
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      p: 1,
                      fontSize: { xs: '0.85rem', sm: '1rem' }
                    }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Grid */}
            <Grid container spacing={1}>
              {calendarDays.map((day, index) => (
                <Grid item xs={12 / 7} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                  >
                    <Box
                      sx={{
                        aspectRatio: '1',
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: day.status === 'empty' ? 'transparent' : theme.palette.grey[100],
                        minHeight: { xs: 70, sm: 80 },
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          bgcolor: day.status !== 'empty' ? theme.palette.grey[200] : 'transparent',
                          cursor: day.status !== 'empty' ? 'pointer' : 'default'
                        }
                      }}
                    >
                      {day.status !== 'empty' && (
                        <>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '0.9rem', sm: '1rem' }
                            }}
                          >
                            {day.day}
                          </Typography>
                          {day.status === 'attended' && (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {getStatusIcon(day.status)}
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.success.main,
                                    fontWeight: 600,
                                    fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                  }}
                                >
                                  {day.accuracy}%
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  textAlign: 'center',
                                  mt: 0.5,
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                {day.poses.length} poses
                              </Typography>
                            </>
                          )}
                          {day.status === 'not-attended' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              {getStatusIcon(day.status)}
                              <Typography
                                variant="caption"
                                sx={{
                                  color: theme.palette.error.main,
                                  fontWeight: 600,
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                }}
                              >
                                Not Attended
                              </Typography>
                            </Box>
                          )}
                          {day.status === 'no-session' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                              }}
                            >
                              No Session
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Legend */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleOutlined style={{ color: theme.palette.success.main }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                  Attended
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CloseCircleOutlined style={{ color: theme.palette.error.main }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                  Not Attended
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 12, height: 12, bgcolor: theme.palette.grey[300], borderRadius: 1 }} />
                <Typography variant="body2" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                  No Session
                </Typography>
              </Stack>
            </Box>
          </CardContent>
        </MainCard>
      )}

      {viewType === 'graphs' && (
        <Grid container spacing={2}>
          {/* Weekly Progress Chart */}
          <Grid item xs={12} lg={8}>
            <MainCard
              title="Weekly Progress - Accuracy & Participation"
              sx={{
                borderRadius: 3,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.grey[200]}`
              }}
            >
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={weeklyData} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
                    <CartesianGrid stroke={theme.palette.grey[300]} strokeDasharray="3 3" />
                    <XAxis dataKey="week" tick={{ fontSize: 12, fill: theme.palette.text.secondary }} />
                    <YAxis
                      domain={[0, 100]}
                      tickFormatter={(tick) => `${tick}%`}
                      tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    />
                    <Tooltip
                      formatter={(value) => `${value}%`}
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.grey[300]}`,
                        borderRadius: 4
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="accuracy"
                      name="Accuracy"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ r: 5, fill: theme.palette.primary.main }}
                      activeDot={{ r: 7 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="participation"
                      name="Participation"
                      stroke={theme.palette.success.main}
                      strokeWidth={2}
                      dot={{ r: 5, fill: theme.palette.success.main }}
                      activeDot={{ r: 7 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </MainCard>
          </Grid>

          {/* Pose Accuracy Chart */}
          <Grid item xs={12} lg={4}>
            <MainCard
              title="Pose Accuracy Breakdown"
              sx={{
                borderRadius: 3,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.grey[200]}`
              }}
            >
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={poseAccuracyData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, x, y, percent }) => (
                        <text
                          x={x}
                          y={y}
                          fill={theme.palette.text.primary}
                          textAnchor="middle"
                          dominantBaseline="central"
                          style={{ fontSize: 12, fontWeight: 500 }}
                        >
                          {`${name} (${Math.round(percent * 100)}%)`}
                        </text>
                      )}
                      outerRadius={80}
                      dataKey="accuracy"
                    >
                      {poseAccuracyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => `${value}%`}
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.grey[200]}`,
                        borderRadius: 4
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </MainCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default ProgressTracker;
